import React, { useState } from 'react';
import { UsersIcon, PlusIcon, EditIcon, TrashIcon, SearchIcon, MailIcon, ShieldCheckIcon, AlertTriangleIcon, CheckCircleIcon, XIcon } from 'lucide-react';
interface User {
  id: number;
  name: string;
  lastName: string;
  email: string;
  phone?: string;
  position?: string;
  company: string;
  role: string;
  status: 'active' | 'inactive';
  lastLogin: string;
}
const UserManagement = () => {
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<User[]>([{
    id: 1,
    name: 'Ana',
    lastName: 'Beltrán',
    email: 'ana.beltran@pymesolutions.com',
    phone: '+52 55 1234 5678',
    position: 'Directora Financiera',
    company: 'PYME Solutions',
    role: 'Administrador',
    status: 'active',
    lastLogin: '2023-07-15 10:30'
  }, {
    id: 2,
    name: 'Carlos',
    lastName: 'Méndez',
    email: 'carlos.mendez@retailexpress.com',
    company: 'Retail Express',
    role: 'Visualizador',
    status: 'active',
    lastLogin: '2023-07-14 15:45'
  }, {
    id: 3,
    name: 'Roberto',
    lastName: 'Sánchez',
    email: 'roberto.sanchez@financepro.com',
    phone: '+52 55 9876 5432',
    position: 'CEO',
    company: 'FinancePro',
    role: 'Super Admin',
    status: 'active',
    lastLogin: '2023-07-15 09:15'
  }]);
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    company: '',
    role: 'Visualizador'
  });
  const handleCreateUser = () => {
    if (formData.name && formData.lastName && formData.email && formData.company) {
      const newUser: User = {
        id: users.length + 1,
        name: formData.name,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || undefined,
        position: formData.position || undefined,
        company: formData.company,
        role: formData.role,
        status: 'active',
        lastLogin: 'Nunca'
      };
      setUsers([...users, newUser]);
      setFormData({
        name: '',
        lastName: '',
        email: '',
        phone: '',
        position: '',
        company: '',
        role: 'Visualizador'
      });
      setShowModal(false);
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
    }
  };
  const handleEditUser = (user: User) => {
    setUserToEdit(user);
    setShowEditModal(true);
  };
  const handleUpdateUser = () => {
    if (userToEdit) {
      setUsers(users.map(u => u.id === userToEdit.id ? userToEdit : u));
      setShowEditModal(false);
      setUserToEdit(null);
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
    }
  };
  const handleDeleteUser = (id: number) => {
    setUserToDelete(id);
    setShowDeleteModal(true);
  };
  const confirmDeleteUser = () => {
    if (userToDelete) {
      setUsers(users.filter(u => u.id !== userToDelete));
      setShowDeleteModal(false);
      setUserToDelete(null);
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
    }
  };
  const getUserToDeleteName = () => {
    const user = users.find(u => u.id === userToDelete);
    return user ? user.name : '';
  };
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'Super Admin':
        return 'bg-gradient-to-r from-purple-600 to-blue-600 text-white';
      case 'Administrador':
        return 'bg-purple-100 text-purple-700';
      case 'Visualizador':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const filteredUsers = users.filter(user => user.name.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase()));
  return <div data-id="element-636">
      {/* Success Toast */}
      {showSuccessToast && <div className="fixed top-4 right-4 z-50 animate-slide-in" data-id="element-637">
          <div className="bg-white border border-green-200 rounded-lg shadow-lg p-4 flex items-center space-x-3 min-w-[300px]" data-id="element-638">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-639">
              <CheckCircleIcon size={20} className="text-green-600" data-id="element-640" />
            </div>
            <div className="flex-1" data-id="element-641">
              <p className="font-medium text-gray-900" data-id="element-642">Usuario eliminado</p>
              <p className="text-sm text-gray-500" data-id="element-643">
                El usuario ha sido eliminado exitosamente
              </p>
            </div>
            <button onClick={() => setShowSuccessToast(false)} className="text-gray-400 hover:text-gray-600" data-id="element-644">
              <XIcon size={18} data-id="element-645" />
            </button>
          </div>
        </div>}
      <div className="mb-6 flex items-center justify-between" data-id="element-646">
        <div data-id="element-647">
          <h1 className="text-2xl font-bold text-gray-800" data-id="element-648">
            Gestión de Usuarios
          </h1>
          <p className="text-gray-600" data-id="element-649">
            Administra todos los usuarios de la plataforma
          </p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-650">
          <PlusIcon size={20} data-id="element-651" />
          <span data-id="element-652">Nuevo usuario</span>
        </button>
      </div>
      {/* Search */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6" data-id="element-653">
        <div className="flex items-center space-x-4" data-id="element-654">
          <div className="flex-1 relative" data-id="element-655">
            <SearchIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-656" />
            <input type="text" placeholder="Buscar usuarios..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-657" />
          </div>
          <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-658">
            <option data-id="element-659">Todos los roles</option>
            <option data-id="element-660">Super Admin</option>
            <option data-id="element-661">Administrador</option>
            <option data-id="element-662">Visualizador</option>
          </select>
        </div>
      </div>
      {/* Users Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-663">
        <div className="overflow-x-auto" data-id="element-664">
          <table className="w-full" data-id="element-665">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-666">
              <tr data-id="element-667">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-668">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-669">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-670">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-671">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-672">
                  Último acceso
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-673">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-674">
              {filteredUsers.map(user => <tr key={user.id} className="hover:bg-gray-50" data-id="element-675">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-676">
                    <div className="flex items-center" data-id="element-677">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.role === 'Super Admin' ? 'bg-gradient-to-r from-purple-600 to-blue-600' : 'bg-blue-100'}`} data-id="element-678">
                        <span className={`text-sm font-medium ${user.role === 'Super Admin' ? 'text-white' : 'text-blue-600'}`} data-id="element-679">
                          {user.name[0]}
                          {user.lastName[0]}
                        </span>
                      </div>
                      <div className="ml-3" data-id="element-680">
                        <div className="flex items-center space-x-2" data-id="element-681">
                          <p className="text-sm font-medium text-gray-900" data-id="element-682">
                            {user.name} {user.lastName}
                          </p>
                          {user.role === 'Super Admin' && <ShieldCheckIcon size={14} className="text-purple-600" data-id="element-683" />}
                        </div>
                        <p className="text-sm text-gray-500" data-id="element-684">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-685">
                    <span className="text-sm text-gray-600" data-id="element-686">
                      {user.company}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-687">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`} data-id="element-688">
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-689">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-690">
                      {user.status === 'active' ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-691">
                    {user.lastLogin}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-692">
                    <div className="flex items-center justify-end space-x-2" data-id="element-693">
                      <button onClick={() => handleEditUser(user)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-694">
                        <EditIcon size={16} data-id="element-695" />
                      </button>
                      {user.role !== 'Super Admin' && <button onClick={() => handleDeleteUser(user.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-696">
                          <TrashIcon size={16} data-id="element-697" />
                        </button>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-698">
          <div className="bg-white rounded-xl p-6 max-w-md w-full" data-id="element-699">
            <div className="flex items-center space-x-3 mb-4" data-id="element-700">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-701">
                <AlertTriangleIcon size={24} className="text-red-600" data-id="element-702" />
              </div>
              <div data-id="element-703">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-704">
                  Eliminar usuario
                </h3>
                <p className="text-sm text-gray-500" data-id="element-705">
                  Esta acción no se puede deshacer
                </p>
              </div>
            </div>
            <p className="text-gray-700 mb-6" data-id="element-706">
              ¿Estás seguro de que deseas eliminar a{' '}
              <span className="font-medium" data-id="element-707">{getUserToDeleteName()}</span>? Esta
              persona ya no podrá acceder a la plataforma.
            </p>
            <div className="flex justify-end space-x-3" data-id="element-708">
              <button onClick={() => {
            setShowDeleteModal(false);
            setUserToDelete(null);
          }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-709">
                Cancelar
              </button>
              <button onClick={confirmDeleteUser} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" data-id="element-710">
                Eliminar usuario
              </button>
            </div>
          </div>
        </div>}
      {/* Edit User Modal */}
      {showEditModal && userToEdit && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-711">
          <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" data-id="element-712">
            <h3 className="text-xl font-semibold mb-4" data-id="element-713">Editar usuario</h3>
            <div className="space-y-4" data-id="element-714">
              <div className="grid grid-cols-2 gap-4" data-id="element-715">
                <div data-id="element-716">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-717">
                    Nombre
                  </label>
                  <input type="text" value={userToEdit.name} onChange={e => setUserToEdit({
                ...userToEdit,
                name: e.target.value
              })} placeholder="Juan" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-718" />
                </div>
                <div data-id="element-719">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-720">
                    Apellidos
                  </label>
                  <input type="text" value={userToEdit.lastName} onChange={e => setUserToEdit({
                ...userToEdit,
                lastName: e.target.value
              })} placeholder="Pérez" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-721" />
                </div>
              </div>
              <div data-id="element-722">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-723">
                  Correo electrónico
                </label>
                <input type="email" value={userToEdit.email} onChange={e => setUserToEdit({
              ...userToEdit,
              email: e.target.value
            })} placeholder="juan@empresa.com" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-724" />
              </div>
              <div data-id="element-725">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-726">
                  Teléfono{' '}
                  <span className="text-gray-400 text-xs" data-id="element-727">(opcional)</span>
                </label>
                <input type="tel" value={userToEdit.phone || ''} onChange={e => setUserToEdit({
              ...userToEdit,
              phone: e.target.value
            })} placeholder="+52 55 1234 5678" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-728" />
              </div>
              <div data-id="element-729">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-730">
                  Cargo{' '}
                  <span className="text-gray-400 text-xs" data-id="element-731">(opcional)</span>
                </label>
                <input type="text" value={userToEdit.position || ''} onChange={e => setUserToEdit({
              ...userToEdit,
              position: e.target.value
            })} placeholder="Director Financiero" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-732" />
              </div>
              <div data-id="element-733">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-734">
                  Rol
                </label>
                <select value={userToEdit.role} onChange={e => setUserToEdit({
              ...userToEdit,
              role: e.target.value
            })} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-735">
                  <option value="Visualizador" data-id="element-736">Visualizador</option>
                  <option value="Administrador" data-id="element-737">Administrador</option>
                  <option value="Super Admin" data-id="element-738">Super Admin</option>
                </select>
              </div>
              {(userToEdit.role === 'Visualizador' || userToEdit.role === 'Administrador') && <div data-id="element-739">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-740">
                    Empresa
                  </label>
                  <select value={userToEdit.company} onChange={e => setUserToEdit({
              ...userToEdit,
              company: e.target.value
            })} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-741">
                    <option value="" data-id="element-742">Seleccionar empresa</option>
                    <option value="PYME Solutions" data-id="element-743">PYME Solutions</option>
                    <option value="Retail Express" data-id="element-744">Retail Express</option>
                    <option value="Manufacturing Pro" data-id="element-745">Manufacturing Pro</option>
                    <option value="Services Plus" data-id="element-746">Services Plus</option>
                  </select>
                </div>}
            </div>
            <div className="flex justify-end space-x-3 mt-6" data-id="element-747">
              <button onClick={() => {
            setShowEditModal(false);
            setUserToEdit(null);
          }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-748">
                Cancelar
              </button>
              <button onClick={handleUpdateUser} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-749">
                Guardar cambios
              </button>
            </div>
          </div>
        </div>}
      {/* Create User Modal */}
      {showModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-750">
          <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" data-id="element-751">
            <h3 className="text-xl font-semibold mb-4" data-id="element-752">Nuevo usuario</h3>
            <div className="space-y-4" data-id="element-753">
              <div className="grid grid-cols-2 gap-4" data-id="element-754">
                <div data-id="element-755">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-756">
                    Nombre
                  </label>
                  <input type="text" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} placeholder="Juan" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-757" />
                </div>
                <div data-id="element-758">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-759">
                    Apellidos
                  </label>
                  <input type="text" value={formData.lastName} onChange={e => setFormData({
                ...formData,
                lastName: e.target.value
              })} placeholder="Pérez" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-760" />
                </div>
              </div>
              <div data-id="element-761">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-762">
                  Correo electrónico
                </label>
                <input type="email" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} placeholder="juan@empresa.com" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-763" />
              </div>
              <div data-id="element-764">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-765">
                  Teléfono{' '}
                  <span className="text-gray-400 text-xs" data-id="element-766">(opcional)</span>
                </label>
                <input type="tel" value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} placeholder="+52 55 1234 5678" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-767" />
              </div>
              <div data-id="element-768">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-769">
                  Cargo{' '}
                  <span className="text-gray-400 text-xs" data-id="element-770">(opcional)</span>
                </label>
                <input type="text" value={formData.position} onChange={e => setFormData({
              ...formData,
              position: e.target.value
            })} placeholder="Director Financiero" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-771" />
              </div>
              <div data-id="element-772">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-773">
                  Rol
                </label>
                <select value={formData.role} onChange={e => setFormData({
              ...formData,
              role: e.target.value
            })} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-774">
                  <option value="Visualizador" data-id="element-775">Visualizador</option>
                  <option value="Administrador" data-id="element-776">Administrador</option>
                  <option value="Super Admin" data-id="element-777">Super Admin</option>
                </select>
              </div>
              {(formData.role === 'Visualizador' || formData.role === 'Administrador') && <div data-id="element-778">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-779">
                    Empresa
                  </label>
                  <select value={formData.company} onChange={e => setFormData({
              ...formData,
              company: e.target.value
            })} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-780">
                    <option value="" data-id="element-781">Seleccionar empresa</option>
                    <option value="PYME Solutions" data-id="element-782">PYME Solutions</option>
                    <option value="Retail Express" data-id="element-783">Retail Express</option>
                    <option value="Manufacturing Pro" data-id="element-784">Manufacturing Pro</option>
                    <option value="Services Plus" data-id="element-785">Services Plus</option>
                  </select>
                </div>}
            </div>
            <div className="flex justify-end space-x-3 mt-6" data-id="element-786">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-787">
                Cancelar
              </button>
              <button onClick={handleCreateUser} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-788">
                Crear usuario
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
export default UserManagement;